import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { AppIcon, type IconName } from '@/components/ui/icons';
import { cn } from '@/lib/utils';
import {
  SHELL_NAV,
  shellNavIconTone,
  shellNavItemTone,
  shellNavLabelTone,
} from '@/lib/layout/shellNav';

export interface ContextualSidebarNavChild {
  to: string;
  label: string;
  icon?: IconName;
  isActive?: boolean;
}

interface ContextualSidebarNavItemProps {
  to: string;
  icon: IconName;
  label: string;
  isActive: boolean;
  isCollapsed: boolean;
  children?: readonly ContextualSidebarNavChild[];
  activeLayoutId: string;
}

const indicatorTransition = {
  duration: 0.16,
  ease: [0.4, 0, 0.2, 1] as const,
};

function SidebarChildLink({
  child,
  compact = false,
  onNavigate,
}: {
  child: ContextualSidebarNavChild;
  compact?: boolean;
  onNavigate?: () => void;
}) {
  return (
    <Link
      to={child.to}
      onClick={onNavigate}
      aria-current={child.isActive ? 'page' : undefined}
      className={cn(
        'group/child relative flex min-w-0 items-center gap-2 rounded-[var(--ax-radius-control)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500/40 focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--ax1-shell-panel)]',
        compact ? 'px-2.5 py-2' : 'px-2 py-1.5',
        child.isActive
          ? 'bg-[var(--sidebar-item-active-bg)] text-[var(--sidebar-item-active-text)]'
          : 'text-[color:var(--ax1-shell-muted)] hover:bg-[var(--sidebar-item-hover-bg)] hover:text-[color:var(--ax1-shell-text)]',
      )}
    >
      {/* The parent owns the selection rail; nested context uses fill and text. */}
      {child.icon && (
        <AppIcon
          name={child.icon}
          className={cn(
            'h-3.5 w-3.5 flex-shrink-0',
            child.isActive
              ? 'text-[var(--sidebar-item-active-icon)]'
              : 'text-[color:var(--ax1-shell-muted)] group-hover/child:text-[color:var(--ax1-shell-text)]',
          )}
        />
      )}
      <span className={cn('truncate font-medium', compact ? 'text-[12px]' : 'text-[11px]')}>
        {child.label}
      </span>
    </Link>
  );
}

/**
 * One governed primary + contextual navigation contract.
 *
 * Expanded desktop and the mobile drawer render children inline below the
 * active parent. A collapsed desktop rail uses a portalled hover/focus flyout,
 * so it is never clipped by the sidebar scroll owner and never consumes page
 * width.
 */
export function ContextualSidebarNavItem({
  to,
  icon,
  label,
  isActive,
  isCollapsed,
  children = [],
  activeLayoutId,
}: ContextualSidebarNavItemProps) {
  const menuId = useId();
  const triggerRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<number | null>(null);
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const [flyoutPosition, setFlyoutPosition] = useState({ left: 0, top: 0 });
  const hasChildren = children.length > 0;

  const clearCloseTimer = () => {
    if (closeTimerRef.current !== null) {
      window.clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openFlyout = () => {
    if (!isCollapsed || !hasChildren) return;
    clearCloseTimer();
    setFlyoutOpen(true);
  };

  const scheduleCloseFlyout = () => {
    clearCloseTimer();
    closeTimerRef.current = window.setTimeout(() => setFlyoutOpen(false), 120);
  };

  useEffect(() => () => clearCloseTimer(), []);

  useEffect(() => {
    if (!flyoutOpen || !isCollapsed) return;

    const updatePosition = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      const panelHeightEstimate = Math.min(children.length * 38 + 48, 420);
      const top = Math.max(8, Math.min(rect.top - 8, window.innerHeight - panelHeightEstimate - 8));
      setFlyoutPosition({ left: rect.right + 8, top });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [children.length, flyoutOpen, isCollapsed]);

  useEffect(() => {
    if (!isCollapsed) setFlyoutOpen(false);
  }, [isCollapsed]);

  const parentLink = (
    <Link
      to={to}
      aria-label={isCollapsed ? label : undefined}
      aria-current={isActive ? 'page' : undefined}
      aria-haspopup={isCollapsed && hasChildren ? 'menu' : undefined}
      aria-expanded={isCollapsed && hasChildren ? flyoutOpen : undefined}
      aria-controls={isCollapsed && hasChildren ? menuId : undefined}
      className={cn(
        isCollapsed ? SHELL_NAV.itemCollapsed : SHELL_NAV.itemExpanded,
        SHELL_NAV.itemFocus,
        shellNavItemTone(isActive, { collapsed: isCollapsed }),
      )}
      onKeyDown={(event) => {
        if (!hasChildren) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          openFlyout();
          window.requestAnimationFrame(() => {
            document.getElementById(menuId)?.querySelector<HTMLElement>('a')?.focus();
          });
        } else if (event.key === 'Escape') {
          setFlyoutOpen(false);
        }
      }}
    >
      {isActive && (
        <motion.div
          layoutId={`${activeLayoutId}-bg`}
          className={isCollapsed ? SHELL_NAV.activeBgCollapsed : SHELL_NAV.activeBg}
          transition={indicatorTransition}
          style={{ originX: 0 }}
        />
      )}
      {isActive && (
        <motion.div
          layoutId={`${activeLayoutId}-rail`}
          className={isCollapsed ? SHELL_NAV.activeDot : SHELL_NAV.activeRail}
          transition={indicatorTransition}
        />
      )}
      <AppIcon name={icon} className={cn(SHELL_NAV.icon, shellNavIconTone(isActive))} />
      {!isCollapsed && (
        <span className={cn(SHELL_NAV.label, shellNavLabelTone(isActive))}>{label}</span>
      )}
      {!isCollapsed && hasChildren && isActive && (
        <AppIcon
          name="chevronDown"
          className="relative z-10 ml-auto h-3.5 w-3.5 text-[color:var(--ax1-shell-muted)]"
        />
      )}
    </Link>
  );

  return (
    <div
      ref={triggerRef}
      className="relative"
      onMouseEnter={openFlyout}
      onMouseLeave={scheduleCloseFlyout}
      onFocusCapture={openFlyout}
      onBlurCapture={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
          scheduleCloseFlyout();
        }
      }}
    >
      {parentLink}

      {!isCollapsed && isActive && hasChildren && (
        <div
          className="ax-divider-start mb-1 ml-[18px] mt-0.5 space-y-0.5 pl-3"
          aria-label={`${label} sections`}
        >
          {children.map((child) => (
            <SidebarChildLink key={child.to} child={child} />
          ))}
        </div>
      )}

      {typeof document !== 'undefined' && isCollapsed && flyoutOpen && hasChildren
        ? createPortal(
          <div
            id={menuId}
            role="menu"
            aria-label={`${label} sections`}
            className={cn(
              'fixed z-[100] w-60 overflow-hidden rounded-xl border border-[color:var(--ax1-shell-border)]',
              'bg-[var(--ax1-shell-panel)] p-2 text-[color:var(--ax1-shell-text)] shadow-xl',
            )}
            style={flyoutPosition}
            onMouseEnter={clearCloseTimer}
            onMouseLeave={scheduleCloseFlyout}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                event.preventDefault();
                setFlyoutOpen(false);
                triggerRef.current?.querySelector<HTMLElement>('a')?.focus();
              }
            }}
          >
            <div className="px-2 pb-2 pt-1 ax-section-label text-[color:var(--ax1-shell-muted)]">
              {label}
            </div>
            <div className="max-h-[min(26rem,calc(100vh-2rem))] space-y-0.5 overflow-y-auto">
              {children.map((child) => (
                <SidebarChildLink
                  key={child.to}
                  child={child}
                  compact
                  onNavigate={() => setFlyoutOpen(false)}
                />
              ))}
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

export default ContextualSidebarNavItem;
